import React from "react";
import { withTranslation } from "react-i18next";
import { t } from "i18next";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Button from "components/CustomButtons/Button";
import CustomSelect from "components/CustomSelect/CustomSelect";

// Builder UI for per-question branching logic. Produces/edits a `logic` object:
//   { rules: [{ op, value, value2?, target }], default: <target | null> }
// where target is 'q:<id>' | 'section:<index>' | 'END' | '' (=> next question).
//
// The available operators and the value input adapt to the question type:
//   - option (yesno, radio, gender, nation, age): is / is not, value from the answer options
//   - multi  (checkbox):                          includes / does not include, value from options
//   - number:                                     = ≠ > ≥ < ≤ between, numeric value(s)
//   - text:                                       is / is not / contains / does not contain
//   - date:                                       on / before / after / between
// Rules evaluate top-to-bottom at answer time (first match wins); `default` is where the flow
// goes when none match (null/'' => the next question in order). Consumed by the answering engine.
const CATEGORY = {
  yesno: "option", radio: "option", gender: "option", nation: "option", age: "option",
  checkbox: "multi",
  number: "number",
  text: "text",
  date: "date",
};

class LogicRules extends React.Component {
  emptyLogic = () => ({ rules: [], default: null });
  getLogic = () => this.props.value || this.emptyLogic();
  category = () => CATEGORY[this.props.questionType] || "option";

  operatorOptions = () => {
    switch (this.category()) {
      case "multi":
        return [{ value: "includes", label: t("includes") }, { value: "nincludes", label: t("does not include") }];
      case "number":
        return [
          { value: "eq", label: "=" }, { value: "ne", label: "≠" },
          { value: "gt", label: ">" }, { value: "gte", label: "≥" },
          { value: "lt", label: "<" }, { value: "lte", label: "≤" },
          { value: "between", label: t("between") },
        ];
      case "text":
        return [
          { value: "eq", label: t("is") }, { value: "ne", label: t("is not") },
          { value: "contains", label: t("contains") }, { value: "ncontains", label: t("does not contain") },
        ];
      case "date":
        return [
          { value: "eq", label: t("on") }, { value: "lt", label: t("before") },
          { value: "gt", label: t("after") }, { value: "between", label: t("between") },
        ];
      default: // option
        return [{ value: "eq", label: t("is") }, { value: "ne", label: t("is not") }];
    }
  };

  usesOptions = () => ["option", "multi"].indexOf(this.category()) >= 0;
  targetSelectOptions = () => ([{ value: "", label: t("Next question (default)") }, ...this.props.targets]);
  findOption = (list, value) => list.filter(o => o.value === value)[0] || null;
  emit = (logic) => { if (this.props.onChange) this.props.onChange(logic); };

  addRule = () => {
    const logic = this.getLogic();
    const defaultOp = this.operatorOptions()[0].value;
    const rules = [...logic.rules, { op: defaultOp, value: null, value2: null, target: "" }];
    this.emit({ ...logic, rules });
  };

  removeRule = (idx) => {
    const logic = this.getLogic();
    this.emit({ ...logic, rules: logic.rules.filter((_, i) => i !== idx) });
  };

  updateRule = (idx, field, value) => {
    const logic = this.getLogic();
    const rules = logic.rules.map((r, i) => (i === idx ? { ...r, [field]: value } : r));
    this.emit({ ...logic, rules });
  };

  setDefault = (target) => this.emit({ ...this.getLogic(), default: target || null });

  inputStyle = () => ({
    fontFamily: "inherit", fontSize: 14, padding: "8px 10px", width: "100%",
    border: "1px solid #cdd8d2", borderRadius: 6, background: "transparent", color: "inherit",
  });

  // The value editor for one rule — depends on the question category and the chosen operator.
  renderValue = (rule, idx) => {
    const cat = this.category();
    const between = rule.op === "between";
    if (cat === "option" || cat === "multi") {
      const options = this.props.currentOptions || [];
      return (
        <CustomSelect
          placeholder={t("value")}
          value={this.findOption(options, rule.value)}
          options={options}
          onChange={(o) => this.updateRule(idx, "value", o ? o.value : null)}
        />
      );
    }
    if (cat === "number") {
      return (
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input type="number" style={this.inputStyle()} value={rule.value == null ? "" : rule.value}
            onChange={(e) => this.updateRule(idx, "value", e.target.value)} placeholder={between ? t("min") : t("value")} />
          {between &&
            <input type="number" style={this.inputStyle()} value={rule.value2 == null ? "" : rule.value2}
              onChange={(e) => this.updateRule(idx, "value2", e.target.value)} placeholder={t("max")} />}
        </div>
      );
    }
    if (cat === "date") {
      return (
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input type="date" style={this.inputStyle()} value={rule.value || ""}
            onChange={(e) => this.updateRule(idx, "value", e.target.value)} />
          {between &&
            <input type="date" style={this.inputStyle()} value={rule.value2 || ""}
              onChange={(e) => this.updateRule(idx, "value2", e.target.value)} />}
        </div>
      );
    }
    // text
    return (
      <input type="text" style={this.inputStyle()} value={rule.value || ""}
        onChange={(e) => this.updateRule(idx, "value", e.target.value)} placeholder={t("value")} />
    );
  };

  render() {
    const { currentOptions, targets } = this.props;
    const logic = this.getLogic();
    const ops = this.operatorOptions();
    const targetOpts = this.targetSelectOptions();
    const needsOptions = this.usesOptions();
    const hasOptions = Array.isArray(currentOptions) && currentOptions.length > 0;
    const canAdd = !needsOptions || hasOptions;

    return (
      <div>
        <p style={{ color: "#666" }}>
          {t("Send the respondent to a different question depending on their answer to this question. Rules are checked top to bottom; the first match wins. If nothing matches, the default applies.")}
        </p>

        {logic.rules.length > 0 &&
          <p style={{ color: "#2f6fed", fontSize: 12.5 }}>
            {t("A question with branching rules is answered before the survey continues.")}
          </p>
        }

        {needsOptions && !hasOptions &&
          <p style={{ color: "#c62828" }}>
            {t("Add this question's answer options first to build branching rules.")}
          </p>
        }

        {logic.rules.map((rule, idx) => (
          <GridContainer key={idx} style={{ alignItems: "center", marginBottom: 4 }}>
            <GridItem xs={12} sm={2} style={{ fontWeight: "bold" }}>{t("If answer")}</GridItem>
            <GridItem xs={6} sm={2}>
              <CustomSelect
                placeholder={t("is")}
                value={this.findOption(ops, rule.op)}
                options={ops}
                onChange={(o) => this.updateRule(idx, "op", o ? o.value : ops[0].value)}
              />
            </GridItem>
            <GridItem xs={6} sm={3}>{this.renderValue(rule, idx)}</GridItem>
            <GridItem xs={12} sm={1} style={{ textAlign: "center", fontWeight: "bold" }}>→</GridItem>
            <GridItem xs={10} sm={3}>
              <CustomSelect
                placeholder={t("go to")}
                value={this.findOption(targetOpts, rule.target || "")}
                options={targetOpts}
                onChange={(o) => this.updateRule(idx, "target", o ? o.value : "")}
              />
            </GridItem>
            <GridItem xs={2} sm={1}>
              <Button size="sm" color="danger" onClick={() => this.removeRule(idx)}>✕</Button>
            </GridItem>
          </GridContainer>
        ))}

        <Button size="sm" color="info" disabled={!canAdd} onClick={this.addRule}>{t("Add rule")}</Button>

        <GridContainer style={{ alignItems: "center", marginTop: 8 }}>
          <GridItem xs={12} sm={3} style={{ fontWeight: "bold" }}>{t("Otherwise, go to")}</GridItem>
          <GridItem xs={12} sm={5}>
            <CustomSelect
              placeholder={t("Next question (default)")}
              value={this.findOption(targetOpts, logic.default || "")}
              options={targetOpts}
              onChange={(o) => this.setDefault(o ? o.value : "")}
            />
          </GridItem>
        </GridContainer>
      </div>
    );
  }
}

export default withTranslation()(LogicRules);
